import React from 'react';
import { useSelector } from 'react-redux';
import { Ad, AppContent, ErrorBoundary, MMPlayerPlaceHolder } from 'mm-ui-components';
import { Store } from 'redux';
import { Theme } from '../../components/connectedComponents/Theme';
import { PostMetaTags } from '../../components/connectedComponents/PostMetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { Page } from '../Page';
import {
  getAuthors,
  getBody,
  getBy,
  getUpdatedText,
  getCover,
  getCreatedAt,
  getCreatedAtISO,
  getFiniteScrollArticles,
  getImageCover,
  getMainCategory,
  getMd5hashPostCanonicalUrl,
  getMetadataDescription,
  getNoIndex,
  getOgDescription,
  getOgImage,
  getOgTitle,
  getOpenWebId,
  getRevcontentWidgetID,
  getSeoDescription,
  getSeoTitle,
  getShareConfig,
  getShowRecommendationsAdvertisement,
  getSlideshowData,
  getSponsor,
  getTags,
  getTitle,
  getTwitterDescription,
  getTwitterImage,
  getTwitterTitle,
  getUpdatedAt,
  getUpdatedAtISO,
  getShowUpdatedAt,
  getDisclaimerText,
  getShouldDisplayDisclaimerAtBottom,
  getShouldAddMMPlayerPlaceholder,
  getShouldSetMinHeightForTopAd,
} from '../../store/template/postPage/postPageOdesa/postPageOdesa.selectors';
import {
  getAdSlotId,
  getAdSlotOnSuccess,
  getCanonicalUrl,
  getSiteName,
  getTemplateName,
} from '../../store/config/config.selectors';
import { SLOT_NAMES } from '../templates.const';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';

import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import { OdesaArticleComponent } from './OdesaArticleComponent';
import { FiniteScroll } from '../../components/finiteScroll/FiniteScroll';

interface PostPageProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

export const PostPageComponent: React.FunctionComponent<PostPageProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <PostPageContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

export const PostPageContentConnected: React.FunctionComponent = () => {
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const sideBar1Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_1));
  const sideBar2Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_2));
  const topAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.TOP_AD));
  const oneOnOneOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.ONE_ON_ONE));
  const sideBar1AdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.SIDE_BAR_1));
  const sideBar2AdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.SIDE_BAR_2));
  const title = useSelector(getTitle);
  const by = useSelector(getBy);
  const updatedText = useSelector(getUpdatedText);
  const authors = useSelector(getAuthors);
  const cover = useSelector(getCover);
  const body = useSelector(getBody);
  const shareConfig = useSelector(getShareConfig);
  const canonical = useSelector(getCanonicalUrl);
  const templateName = useSelector(getTemplateName);
  const siteName = useSelector(getSiteName);
  const sponsor = useSelector(getSponsor);
  const showRecommendationsAdvertisement = useSelector(getShowRecommendationsAdvertisement);
  const revcontentWidgetID = useSelector(getRevcontentWidgetID);
  const slideshow = useSelector(getSlideshowData);
  const openWebId = useSelector(getOpenWebId);
  const tags = useSelector(getTags);
  const mainCategory = useSelector(getMainCategory);
  const pageDescription = useSelector(getMetadataDescription);
  const seoTitle = useSelector(getSeoTitle) || title;
  const pageImage = useSelector(getImageCover);
  const createdAt = useSelector(getCreatedAt);
  const createdAtISO = useSelector(getCreatedAtISO);
  const updatedAt = useSelector(getUpdatedAt);
  const updatedAtISO = useSelector(getUpdatedAtISO);
  const showUpdatedAt = useSelector(getShowUpdatedAt);
  const noIndex = useSelector(getNoIndex);
  const ogDescription = useSelector(getOgDescription);
  const ogTitle = useSelector(getOgTitle);
  const ogImage = useSelector(getOgImage);
  const twitterDescription = useSelector(getTwitterDescription);
  const twitterTitle = useSelector(getTwitterTitle);
  const twitterImage = useSelector(getTwitterImage);
  const seoDescription = useSelector(getSeoDescription);
  const articleSection = mainCategory ? mainCategory.displayName : '';
  const md5hashPostCanonicalUrl = useSelector(getMd5hashPostCanonicalUrl);
  const finiteScrollArticles = useSelector(getFiniteScrollArticles);
  const disclaimerText = useSelector(getDisclaimerText);
  const shouldDisplayDisclaimerAtBottom = useSelector(getShouldDisplayDisclaimerAtBottom);
  const shouldAddMMPlayerPlaceholder = useSelector(getShouldAddMMPlayerPlaceholder);
  const shouldSetMinHeightForTopAd = useSelector(getShouldSetMinHeightForTopAd);

  const [mainArticleURL, setMainArticleURL] = React.useState('');

  React.useEffect(() => {
    if (!mainArticleURL && window) {
      setMainArticleURL(window.location.href);
    }
  }, [mainArticleURL]);

  return (
    <React.Fragment>
      <PostMetaTags
        {...{
          pageDescription,
          seoTitle,
          pageImage,
          createdAtISO,
          updatedAtISO,
          noIndex,
          ogDescription,
          ogTitle,
          ogImage,
          twitterDescription,
          twitterTitle,
          twitterImage,
          seoDescription,
          articleSection,
          authors,
          prevSlideshowUrl: slideshow && slideshow.prevLink,
          nextSlideshowUrl: slideshow && slideshow.nextLink,
          tags,
        }}
      />
      <HeadScripts />
      <Theme>
        <AppContent>
          <NavigationLayout mainWrapperConfigThemeKey="mainArticleContent">
            <Ad id={topAdId} onSuccess={topAdOnSuccess} setMinHeight={shouldSetMinHeightForTopAd} />
            {shouldAddMMPlayerPlaceholder ? <MMPlayerPlaceHolder smallAndMediumScreens /> : null}
            <FiniteScroll
              articles={finiteScrollArticles}
              articleComponent={OdesaArticleComponent}
              by={by}
              siteName={siteName}
              templateName={templateName}
              revcontentWidgetID={revcontentWidgetID}
              openWebId={openWebId}
              shouldDisplayDisclaimerAtBottom={shouldDisplayDisclaimerAtBottom}
            >
              <OdesaArticleComponent {...{
                mainCategory,
                title,
                by,
                updatedText,
                updatedAt,
                updatedAtISO,
                authors,
                shareConfig,
                canonical,
                cover,
                siteName,
                templateName,
                sponsor,
                slideshow,
                body,
                sideBar1Id,
                sideBar2Id,
                showRecommendationsAdvertisement,
                revcontentWidgetID,
                createdAt,
                createdAtISO,
                showUpdatedAt,
                tags,
                md5hashPostCanonicalUrl,
                openWebId,
                sideBar1AdOnSuccess,
                sideBar2AdOnSuccess,
                mainArticleURL,
                disclaimerText,
                shouldDisplayDisclaimerAtBottom,
                addMMPlayerPlaceholder: shouldAddMMPlayerPlaceholder,
                addOpenwebLiveblogPlaceholder: true,
              }}
              />
            </FiniteScroll>
            <Ad id={oneOnOneId} onSuccess={oneOnOneOnSuccess} />
          </NavigationLayout>
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};
